import { openDB } from "idb/with-async-ittr";
import idxDB from "../constants/eVtrIdxDB";
import { generateDecryptValue, generateEncryptedValue } from "../constants/vtrTrip";
import info from "../../package.json";
import {
  QUOTA_EXCEEDED_ERROR,
  QUOTA_EXCEEDED_ALERT,
  UNEXPECTED_ERROR,
} from "../constants/ErrorText";
import { getSizeOfObject, storageCheck } from "../enhancers/utils";

export async function createDB() {
  if (!("indexedDB" in window)) {
    // TODO: add some error message to be returned
    console.log("This browser doesn't support IndexedDB");
    return;
  }
  // * Logging current size of local storage
  const { details, spaceAvailable } = await storageCheck();
  console.info("[createDB]", details);
  console.info("[createDB] Space available? ", spaceAvailable);

  const start = performance.now();
  const dbPromise = await openDB(idxDB.name, idxDB.version, {
    upgrade: async (db, oldVersion, newVersion, transaction) => {
      console.info(`IndexedDB new version: ${newVersion}.`);
      // When a new version is released the break statement is removed
      // from the latest case and added to the new one that is required.
      // This will prevent issues while updating from older DB versions.
      switch (oldVersion) {
        case 0:
          console.info("Creating indexedDB.");
          await createStores();
        // falls through
        case 1:
          // * Re-creating Species/Timestamp data
          await updateData("species");
          await updateData("reportTripType");
          await updateData("timestamp");
        // falls through
        case 2:
          // * Updating timestamp for ChartArea/Gear/Port
          await updateData("timestamp");
        // falls through
        case 3:
        // * Re-creating all as quickfix
        // falls through
        case 4:
          // * Adding Lobster trips info
          await updateData("reportTripType");
        // falls through
        case 5:
          // * Re-creating gear info
          await updateData("gear");
        // falls through
        case 6:
        case 7:
          // * Re-creating ReportTripType data
          await updateData("reportTripType");
        // falls through
        case 8:
          // * Re-creating Species info
          await updateData("species");
        // falls through
        case 9:
          // * Re-creating gear info for lobster questions
          await updateData("gear");
        // falls through
        case 10:
          // * Updating Report Trip Type information for Lobster trips
          await updateData("reportTripType");
        // falls through
        case 11:
          // * Re-creating gear info
          // * Adding UOM store
          await updateData("gear");
          await updateData("uomCodes");
        // falls through
        case 12:
          // * Re-creating port info, adding disabled key info
          await updateData("port");
        // falls through
        case 13:
          // * Re-creating species info, adding disabled key info
          await updateData("species");
        // falls through
        case 14:
          // * Rebuilding species info, fixing issue with missing keys
          await updateData("species");
        // falls through
        case 15:
          // * Re-creating dealers info, addingg 0 unknown record
          await updateData("dealers");
          break;
        default:
          console.error("Unknown DB Version");
      }

      async function createStores() {
        for (const { name, key, storeData, visible, config } of idxDB.stores) {
          if (!db.objectStoreNames.contains(name)) {
            const store = db.createObjectStore(name, { ...config });
            // * Create an index to search by store key
            store.createIndex(key, key, { unique: true });
            if (visible) {
              console.info(`Populating ${name} store!`);
              const tx = transaction.objectStore(name);
              await Promise.all([
                Array.isArray(storeData)
                  ? storeData.map((data) => {
                      return tx.put(data);
                    })
                  : tx.put(storeData),
                tx.done,
              ]).catch((err) => console.error("An error occurred.", err));
            }
          }
        }
      }

      async function updateData(storeName) {
        if (!transaction.objectStoreNames.contains(storeName)) {
          console.info(`Store ${storeName} does not exist, calling createStores method.`);
          return await createStores();
        }
        const tx = transaction.objectStore(storeName);
        tx.clear();
        for (const { name, storeData } of idxDB.stores) {
          if (name === storeName) {
            console.info(`Updating ${name} store!`);
            await Promise.all([
              Array.isArray(storeData)
                ? storeData.map((data) => {
                    return tx.put(data);
                  })
                : tx.put(storeData),
              tx.done,
            ]).catch((err) => console.error("An error occurred.", err));
          }
        }
      }
      // * Added block to handle local storage issues initially
      transaction.onabort = (event) => {
        const error = event.target.error;
        const errorMessage =
          error.name === QUOTA_EXCEEDED_ERROR || error.code === 22
            ? QUOTA_EXCEEDED_ALERT
            : UNEXPECTED_ERROR;

        console.group("onAbort");
        console.info(error);
        console.groupEnd();
        if (error.name === QUOTA_EXCEEDED_ERROR) {
          alert(errorMessage);
        }
        throw Error(errorMessage);
      };
    },
  });
  const end = performance.now();
  console.log(`Call to createDB took ${Math.round(end - start) / 1000} milliseconds`);
  dbPromise.close();
}

export async function updateStoresData(response, username, password) {
  const dbPromise = await openDB(idxDB.name, idxDB.version);

  try {
    // checking size of the response to be stored and localStorage current size
    const currentStorageSize = await storageCheck();
    console.info("Do we have space? ", currentStorageSize);

    const objectSize = getSizeOfObject(response);
    console.info("Response size to be stored: ", objectSize.toFixed(2), response);

    // updating stores data with api response
    await updateTimestamp(dbPromise, response);

    // storing user info
    await storeUserCredentials(dbPromise, response, username, password);
  } catch (error) {
    console.error("[updateStoresData] Failed to complete the transaction.", error);
    const errorMessage =
      error.name === QUOTA_EXCEEDED_ERROR || error.code === 22
        ? QUOTA_EXCEEDED_ALERT
        : UNEXPECTED_ERROR;
    throw Error(errorMessage);
  }

  // closing connection
  dbPromise.close();
}

const updateTimestamp = async (db, data) => {
  let start = performance.now();
  const tx = db.transaction("timestamp", "readwrite");
  const index = tx.store.index("id");
  const chainStr = [
    "dealers",
    "operators",
    "permits",
    "species",
    "chartarea",
    "gear",
    "port",
    "uom",
  ];
  let newTimestamp = {};
  let timestampObj = {};

  Object.entries(data).forEach(([key]) => {
    if (chainStr.some((el) => key.toLowerCase().startsWith(el))) {
      newTimestamp[key] = data[key].timestamp;
      timestampObj[key] = data[key][0];
    }
  });
  // * updating timestamp store data
  for await (const cursor of index.iterate()) {
    cursor.update({ ...cursor.value, ...newTimestamp });
  }
  await tx.done;

  // * updating dealers/operator/permit/species/gear/chartArea/port/uomCodes store data
  for (const [key, value] of Object.entries(timestampObj)) {
    let storeName = "";
    if (key.toLowerCase().startsWith("dealers")) {
      storeName = "dealers";
    } else if (key.toLowerCase().startsWith("operators")) {
      storeName = "operator";
    } else if (key.toLowerCase().startsWith("permits")) {
      storeName = "permit";
    } else if (key.toLowerCase().startsWith("species")) {
      storeName = "species";
    } else if (key.toLowerCase().startsWith("gear")) {
      storeName = "gear";
    } else if (key.toLowerCase().startsWith("chart")) {
      storeName = "chartArea";
    } else if (key.toLowerCase().startsWith("port")) {
      storeName = "port";
    } else if (key.toLowerCase().startsWith("uom")) {
      storeName = "uomCodes";
    }
    console.info(`Updating ${storeName} store!`);
    const tx = db.transaction(storeName, "readwrite");
    // * Clearing charArea indexedDB store data
    if (storeName === "chartArea") {
      tx.store.clear();
    }
    await Promise.all([
      value.map((data) => {
        return tx.store.put(data);
      }),
      tx.done,
    ]).catch((err) => console.log("An error occurred.", err));
  }

  let end = performance.now();
  console.log(
    `Call to updateTimestamp took ${Math.round(end - start) / 1000} milliseconds`
  );
};

export const getTimestamp = async () => {
  const db = await openDB(idxDB.name, idxDB.version);
  return await db.getAllFromIndex("timestamp", "id").then((timestamp) => {
    let timestampObj = {};
    timestamp.forEach((obj) => {
      Object.entries(obj).forEach(([key, value]) => {
        if (key !== "id") {
          timestampObj[key] = value;
        }
      });
    });
    return timestampObj;
  });
};

const formatTimestamp = (data) => {
  let timestampObj = {};
  data.forEach((obj) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (key !== "id") {
        timestampObj[key] = value;
      }
    });
  });
  return timestampObj;
};

const storeUserCredentials = async (db, response, username, password) => {
  const isUserStored = await getUser(username);
  const userObj = {
    client_id: generateEncryptedValue(response.client_id, info.created_by),
    username: generateEncryptedValue(username.toLowerCase(), info.created_by),
    password: generateEncryptedValue(password, info.created_by),
    user_full_name: generateEncryptedValue(response.user_full_name, info.created_by),
    user_app_status: generateEncryptedValue(response.user_app_status, info.created_by),
    operator_key: generateEncryptedValue(response.operator_key, info.created_by),
    entitlements: generateEncryptedValue(
      JSON.stringify(response.entitlements),
      info.created_by
    ),
    vtr_preferences: generateEncryptedValue(
      JSON.stringify(response.vtr_preferences),
      info.created_by
    ),
  };
  if (isUserStored !== undefined) {
    const tx = db.transaction("users", "readwrite");
    const index = tx.store.index("username");
    for await (const cursor of index.iterate(username)) {
      cursor.update({ ...cursor.value, ...userObj });
    }
    console.info("Existing user!");
    return await tx.done;
  } else {
    console.info("First time user!");
    return await db.put("users", userObj);
  }
};

export const getUser = async (username) => {
  const db = await openDB(idxDB.name, idxDB.version);
  const encryptedUser = generateEncryptedValue(username.toLowerCase(), info.created_by);

  return await db.getFromIndex("users", "username", encryptedUser);
};

export async function retrieveUserCredentials(username, password) {
  const storedUserObj = await getUser(username);
  const storedData = await getExistingData();
  let data = {};
  let userObj = {};

  if (storedUserObj !== undefined) {
    const unencryptedPassword = generateDecryptValue(
      storedUserObj.password,
      info.created_by
    );
    if (unencryptedPassword === password) {
      userObj = {
        client_id: generateDecryptValue(storedUserObj.client_id, info.created_by),
        username: generateDecryptValue(storedUserObj.username, info.created_by),
        user_full_name: generateDecryptValue(
          storedUserObj.user_full_name,
          info.created_by
        ),
        user_app_status: generateDecryptValue(
          storedUserObj.user_app_status,
          info.created_by
        ),
        operator_key: generateDecryptValue(storedUserObj.operator_key, info.created_by),
        entitlements: JSON.parse(
          generateDecryptValue(storedUserObj.entitlements, info.created_by)
        ),
        vtr_preferences: JSON.parse(
          generateDecryptValue(storedUserObj.vtr_preferences, info.created_by)
        ),
      };
      data.userPayload = { ...userObj };
      data.validationsData = { ...storedData };
    }
  }

  return data;
}

export const getExistingData = async () => {
  const dbPromise = await openDB(idxDB.name, idxDB.version);
  let validation = {};

  // getting initial data from indexedDB stores
  const start = performance.now();
  for (const { name, key, visible } of idxDB.stores) {
    if (dbPromise.objectStoreNames.contains(name) && visible) {
      let storeData = await dbPromise.getAllFromIndex(name, key);
      validation[name] = storeData;
      if (name === "timestamp") validation[name] = formatTimestamp(storeData);
    }
  }
  const end = performance.now();
  console.log(
    `Call to get existing data took ${Math.round(end - start) / 1000} milliseconds`
  );
  // closing db connection
  dbPromise.close();

  return validation;
};
